'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import type { ComponentProps } from 'react';
import * as React from 'react';
import { cn } from 'src/app/lib/utils/tailwindcss';

interface CheckboxContextValue {
  isChecked: CheckboxPrimitive.CheckedState;
  isDisabled?: boolean;
}

const CheckboxContext = React.createContext<CheckboxContextValue | null>(null);

export type BaseCheckboxProps = ComponentProps<typeof CheckboxPrimitive.Root>;

function BaseCheckbox({ className, checked, defaultChecked, children, ...props }: BaseCheckboxProps) {
  const [internalChecked, setInternalChecked] = React.useState(defaultChecked ?? false);
  const isControlled = checked !== undefined;
  const isChecked = isControlled ? checked : internalChecked;
  const isDisabled = props.disabled;

  return (
    <CheckboxContext.Provider value={{ isChecked, isDisabled }}>
      <CheckboxPrimitive.Root
        data-slot="checkbox"
        className={cn('cursor-pointer', className)}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={(value) => {
          if (!isControlled) setInternalChecked(!!value);
          if (props.onCheckedChange) props.onCheckedChange(value);
        }}
        {...props}
      >
        {children}
      </CheckboxPrimitive.Root>
    </CheckboxContext.Provider>
  );
}

function useCheckboxState() {
  const ctx = React.useContext(CheckboxContext);
  if (!ctx) throw new Error('useCheckboxState must be used within Checkbox');
  return ctx;
}

export { BaseCheckbox, useCheckboxState };
